import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import DOMPurify from "dompurify";
import axios from "axios";
import BeforeAfterSlider from "./BeforeAfterSlider";
import { 
  ArrowLeft, CheckCircle, Phone, Mail, Home, Building2, 
  Sparkles, Droplets, Sun, Shirt, Clock, Shield, Users,
  Leaf, Zap, ThumbsUp, Calendar, MessageCircle, X, Send,
  ShieldAlert, Pipette, AlertTriangle, Biohazard, HardHat, KeyRound, Brush,
  Star, ChevronRight
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageProvider";
import Seo from "../seo/Seo";
import { getPageMeta } from "../seo/siteConfig";
import { buildServiceSchema, buildFaqSchema } from "../seo/serviceSchemas";
import serviceTranslations from "../serviceTranslations";
import PrijstabelNaVerhuur from "./PrijstabelNaVerhuur";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "/logo-clean-force.png";

// Service data
const servicesData = {
  "vakantiehuizen": {
    title: "Vakantiehuizen Service",
    subtitle: "Complete schoonmaak, wasserij én check-in voor vakantiewoningen",
    icon: Home,
    image: "https://images.unsplash.com/photo-1759264244726-adde4e4318fc?crop=entropy&cs=srgb&fm=jpg&w=1200",
    intro: "Als verhuurder van een vakantiewoning weet u hoe belangrijk een schone en gastvrije ruimte is voor positieve recensies en terugkerende gasten. Onze Vakantiehuizen Service biedt een complete oplossing: professionele schoonmaak, wasserijdiensten én check-in van gasten tussen elke wisseling.",
    benefits: [
      { icon: Clock, title: "Tijdsbesparing", text: "U hoeft niet meer zelf schoon te maken, was te doen of gasten op te vangen. Wij regelen alles tussen de boekingen." },
      { icon: ThumbsUp, title: "Hogere Recensies", text: "Een brandschone woning en persoonlijke ontvangst leiden tot betere reviews en meer boekingen." },
      { icon: Calendar, title: "Flexibele Planning", text: "Wij stemmen af op uw boekingskalender, ook bij last-minute wisselingen." },
      { icon: Shield, title: "Betrouwbaar Team", text: "Vaste medewerkers die uw woning kennen en volgens checklist werken." }
    ],
    whatWeOffer: [
      "Grondige schoonmaak van alle kamers, keuken en badkamers",
      "Beddengoed verschonen met vers, gewassen linnengoed",
      "Handdoeken vervangen en netjes ophangen",
      "Prullenbakken legen en afval verwijderen",
      "Voorraden controleren (toiletpapier, zeep, koffie etc.)",
      "Apparatuur checken (koelkast, oven, koffiemachine)",
      "Ramen lappen indien nodig",
      "Wasserij: al het gebruikte textiel wassen, drogen en strijken",
      "Kwaliteitscontrole met foto-rapportage",
      "Check-in gasten: persoonlijke ontvangst met sleuteloverdracht en rondleiding",
      "Check-in gasten: sleutel klaarzetten in keybox met instructies"
    ],
    pricing: `
      <strong>ALLES-IN PRIJS per wisseling (excl. BTW):</strong><br>
      <span style="color: #A1A1AA;">Inclusief: schoonmaak, bedden opmaken, handdoeken, keuken, badkamer.</span><br><br>
      <strong>Studio/appartement tot 50m²:</strong> 1 slpk: €65<br>
      <strong>Appartement 50-80m²:</strong> 1 slpk: €80 / 2 slpk: €95<br>
      <strong>Woning 80-120m²:</strong> 2 slpk: €120 / 3 slpk: €145 / 4 slpk: €160<br>
      <strong>Grote woning 120-180m²:</strong> 3 slpk: €175 / 4 slpk: €195 / 5 slpk: €220<br>
      <strong>Villa >180m²:</strong> vanaf €230 — offerte op maat<br><br>
      <hr style="border-color: rgba(255,255,255,0.2); margin: 16px 0;">
      <strong>CHECK-IN SERVICE GASTEN (apart in te boeken):</strong><br><br>
      <strong>Optie 1 - Persoonlijke Ontvangst:</strong> €45,00<br>
      <span style="color: #A1A1AA;">Medewerker verwelkomt de gasten, overhandigt sleutels, geeft rondleiding en legt huisregels uit. (1 uur)</span><br><br>
      <strong>Optie 2 - Sleuteloverdracht:</strong> €20,00<br>
      <span style="color: #A1A1AA;">Sleutel klaarzetten in keybox/afgiftepunt, instructies achterlaten.</span><br><br>
      <hr style="border-color: rgba(255,255,255,0.2); margin: 16px 0;">
      <strong>WASSERIJ TOESLAG:</strong> €6,00/kg<br><br>
      <strong>KORTINGEN:</strong><br>
      - Check-in + schoonmaak op dezelfde dag: €10,00 korting op check-in<br>
      - 10+ wisselingen/maand: 15% korting<br>
      - 20+ wisselingen/maand: 20% korting
    `
  },
  "kantoorschoonmaak": {
    title: "Kantoorschoonmaak",
    subtitle: "Professionele reiniging voor een productieve werkomgeving",
    icon: Building2,
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200",
    intro: "Een schoon kantoor is essentieel voor de gezondheid, productiviteit en uitstraling van uw bedrijf. Onze professionele kantoorschoonmaak zorgt dagelijks of periodiek voor een hygiënische en representatieve werkplek.",
    benefits: [
      { icon: Users, title: "Gezondere Werknemers", text: "Minder ziekteverzuim door een hygiënische werkomgeving." },
      { icon: Sparkles, title: "Professionele Uitstraling", text: "Maak indruk op klanten en bezoekers met een verzorgd kantoor." },
      { icon: Clock, title: "Flexibele Tijden", text: "Schoonmaak buiten kantooruren zodat u niet gestoord wordt." },
      { icon: Shield, title: "Betrouwbaar & Discreet", text: "Gescreend personeel dat vertrouwelijk omgaat met uw bedrijfsruimte." }
    ],
    whatWeOffer: [
      "Dagelijks stofzuigen en dweilen van vloeren",
      "Afstoffen van bureaus, kasten en oppervlakken",
      "Legen van prullenbakken en papierbakken",
      "Schoonmaken van sanitaire ruimtes",
      "Reinigen van keuken en kantine",
      "Glazenwassen (binnen en buiten)",
      "Dieptereiniging van tapijt en vloerbedekking",
      "Periodieke grote schoonmaak",
      "Desinfectie van contactpunten (deurklinken, lichtschakelaars)"
    ],
    pricing: "Prijs afhankelijk van kantoorgrootte, frequentie en gewenste werkzaamheden. Vraag een offerte aan."
  },
  "industriele-reiniging": {
    title: "Industriële Reiniging",
    subtitle: "Specialistische schoonmaak voor fabrieken en magazijnen",
    icon: Sparkles,
    image: "https://images.unsplash.com/photo-1627905646269-7f034dcc5738?w=1200",
    intro: "Industriële omgevingen stellen hoge eisen aan schoonmaak. Van productiehallen tot magazijnen - onze specialisten beschikken over de kennis en apparatuur voor grondige, veilige en efficiënte reiniging.",
    benefits: [
      { icon: Shield, title: "Veiligheid Voorop", text: "Voorkom ongevallen door gladde vloeren of rondslingerend afval." },
      { icon: Zap, title: "Minder Storingen", text: "Schone machines en installaties werken beter en gaan langer mee." },
      { icon: CheckCircle, title: "Normconform", text: "Voldoe aan HACCP, IFS en andere hygiënenormen." },
      { icon: Clock, title: "Minimale Downtime", text: "Wij werken in de nacht of tijdens daluren." }
    ],
    whatWeOffer: [
      "Reinigen van machines en productielijnen",
      "Schoonmaken van magazijnstellingen en legborden",
      "Vloerreiniging met industriële schrobmachines",
      "Stoomreiniging voor desinfectie zonder chemicaliën",
      "Hogedrukreiniging van oppervlakken",
      "Verwijderen van olie, vet en productieresten",
      "Reiniging van leidingen, tanks en toebehoren",
      "Afvalbeheer en opruimen van rommel",
      "Periodieke dieptereiniging"
    ],
    pricing: "Prijs op aanvraag, afhankelijk van oppervlakte, type vervuiling en frequentie."
  },
  "ramenwassen-osmose": {
    title: "Ramenwassen met Osmose",
    subtitle: "Streepvrij resultaat met zuiver osmosewater",
    icon: Droplets,
    image: "https://images.unsplash.com/photo-1746905205773-3ea50c8cd808?w=1200",
    intro: "Osmose ramenwassen is de moderne, milieuvriendelijke methode voor kristalheldere ramen. Bij dit proces wordt leidingwater onder hoge druk door een fijn membraan geperst, waardoor vrijwel alle mineralen en onzuiverheden worden gefilterd. Het resultaat: extreem zuiver water dat vuil van het glas trekt en streepvrij opdroogt — zonder enig schoonmaakmiddel.",
    warning: "Osmosewater mag NIET worden gebruikt op goudkleurige aluminium raamkozijnen! Het zuivere water kan de goudkleurige coating beschadigen. Heeft u goudkleurige kozijnen? Neem contact op voor een alternatieve oplossing.",
    benefits: [
      { icon: Sparkles, title: "100% Streepvrij", text: "Osmosewater droogt op zonder strepen of kalkvlekken." },
      { icon: Leaf, title: "Milieuvriendelijk", text: "Geen chemicaliën, veilig voor mens, dier en plant." },
      { icon: Clock, title: "Langer Schoon", text: "Ramen blijven langer schoon omdat geen zeepresten vuil aantrekken." },
      { icon: Shield, title: "Veilig Werken", text: "Tot 6 meter hoogte zonder ladder dankzij telescoopsysteem." }
    ],
    whatWeOffer: [
      "Ramenwassen met gezuiverd osmosewater",
      "Telescopisch systeem tot 6 meter hoogte - zonder ladder",
      "Alle soorten ramen: standaard, schuifpuien, dakramen",
      "Kozijnen en raamtabletten inbegrepen",
      "Geen chemicaliën of agressieve middelen",
      "Geschikt voor woningen, kantoren en winkels",
      "Abonnementen met korting mogelijk",
      "Alternatief voor goudkleurige aluminium kozijnen beschikbaar"
    ],
    pricing: `
      <div style="background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.3); border-radius: 8px; padding: 16px; margin-bottom: 20px;">
        <strong style="color: #EF4444;">⚠️ LET OP - GOUDKLEURIG ALUMINIUM</strong><br>
        <span style="color: #FCA5A5;">Osmosewater mag NIET worden gebruikt op goudkleurige aluminium raamkozijnen. Bij goudkleurig aluminium werken wij traditioneel manueel (zie prijzen hieronder).</span>
      </div>
      
      <strong>OSMOSE PRIJZEN per raam (excl. BTW):</strong><br>
      <em>Standaard methode - niet bij goudkleurig aluminium</em><br><br>
      
      <strong>Standaard raam (tot 1,5m²):</strong><br>
      • Buitenkant: €4,50<br>
      • Binnen: €3,00<br>
      • Binnen + buiten: €7,00<br><br>
      
      <strong>Groot raam (schuifpui, 1,5-3m²):</strong><br>
      • Buitenkant: €9,00<br>
      • Binnen: €5,00<br>
      • Binnen + buiten: €13,00<br><br>
      
      <strong>Velux/dakraam:</strong><br>
      • Buitenkant: €10,00<br>
      • Binnen + buiten: €15,00<br><br>
      
      <hr style="border-color: rgba(255,255,255,0.2); margin: 20px 0;">
      
      <strong>TRADITIONEEL MANUEEL per raam (excl. BTW):</strong><br>
      <em>Voor goudkleurig aluminium kozijnen</em><br><br>
      
      <strong>Standaard raam (tot 1,5m²):</strong><br>
      • Buitenkant: €5,50<br>
      • Binnen: €3,50<br>
      • Binnen + buiten: €8,50<br><br>
      
      <strong>Groot raam (schuifpui, 1,5-3m²):</strong><br>
      • Buitenkant: €11,00<br>
      • Binnen: €6,00<br>
      • Binnen + buiten: €16,00<br><br>
      
      <strong>Velux/dakraam:</strong><br>
      • Buitenkant: €12,00<br>
      • Binnen + buiten: €18,00<br><br>
      
      <hr style="border-color: rgba(255,255,255,0.2); margin: 20px 0;">
      
      <strong>EXTRA GROTE RAMEN (≥ 4m², etalages, puien, glasgevels) per m²:</strong><br><br>
      
      <strong>Osmose (excl. BTW):</strong><br>
      • Buitenkant: €2,00–€2,50/m²<br>
      • Volume/bedrijven (veel glas): €1,60–€2,00/m²<br>
      • Binnen + buiten: €3,00–€4,00/m²<br><br>
      
      <strong>Traditioneel manueel (excl. BTW):</strong><br>
      • Buitenkant: €2,50–€3,00/m²<br>
      • Binnen + buiten: €3,50–€4,50/m²<br><br>
      
      <hr style="border-color: rgba(255,255,255,0.2); margin: 20px 0;">
      
      Minimumtarief: €35,00 per bezoek<br>
      Voorrijkosten: gratis tot 15 km
    `
  },
  "zonnepanelen-reiniging": {
    title: "Zonnepanelen Reiniging",
    subtitle: "Verhoog uw rendement tot 30% met schone panelen",
    icon: Sun,
    image: "https://images.unsplash.com/photo-1754619880959-2b0528375883?w=1200",
    intro: "Vuile zonnepanelen produceren minder energie. Stof, vogelpoep, bladeren en Saharazand kunnen het rendement met 5-20% verminderen. Onze professionele reiniging met osmosewater herstelt de optimale werking van uw installatie.",
    benefits: [
      { icon: Zap, title: "Meer Opbrengst", text: "Schone panelen leveren tot 15-20% meer energie op." },
      { icon: Shield, title: "Langere Levensduur", text: "Voorkom permanente schade door vogelpoep en vuil." },
      { icon: Leaf, title: "Osmosewater", text: "Geen chemicaliën, veilig voor uw panelen en het milieu." },
      { icon: CheckCircle, title: "Garantiebehoud", text: "Veel fabrikanten eisen onderhoud voor garantie." }
    ],
    whatWeOffer: [
      "Reiniging met puur osmosewater (geen chemicaliën)",
      "Veilig werken met telescoopsysteem",
      "Inspectie op zichtbare schade",
      "Geschikt voor alle soorten panelen",
      "Woning- en bedrijfsinstallaties",
      "Jaarlijks onderhoudsabonnement mogelijk",
      "Flexibele planning in voorjaar of nazomer"
    ],
    pricing: "Prijs afhankelijk van aantal panelen en bereikbaarheid. Gemiddeld €3-5 per paneel. Vraag een offerte aan."
  },
  "wasserijdiensten": {
    title: "Wasserijdiensten",
    subtitle: "Professionele textielverzorging via HomeCareService",
    icon: Shirt,
    image: "https://images.unsplash.com/photo-1545173168-9f1947eebb7f?w=1200",
    intro: "Onze wasserijdiensten via HomeCareService bieden professionele textielverzorging voor bedrijven. Van werkkleding tot linnengoed - wij zorgen voor het ophalen, wassen, drogen, strijken en terugbrengen van uw textiel.",
    benefits: [
      { icon: Clock, title: "Tijdsbesparing", text: "Focus op uw kernactiviteiten, wij regelen de was." },
      { icon: Sparkles, title: "Professionele Kwaliteit", text: "Industriële machines en expertise voor perfect resultaat." },
      { icon: Leaf, title: "Milieubewust", text: "Energiezuinige machines en milieuvriendelijke middelen." },
      { icon: Users, title: "Persoonlijke Service", text: "Vaste contactpersoon en flexibele ophaalschema's." }
    ],
    whatWeOffer: [
      "Ophalen en terugbrengen op locatie",
      "Wassen, drogen en strijken",
      "Werkkleding en uniformen",
      "Beddengoed en handdoeken",
      "Tafellakens en servetten (horeca)",
      "Inspectie en kleine reparaties",
      "Flexibele frequentie: dagelijks tot wekelijks"
    ],
    pricing: `
      <strong>B2B Tarief:</strong><br>
      Wassen, drogen én strijken: <strong>€6,- per kg</strong> (excl. BTW)<br><br>
      <a href="https://homecareservice.me/prijslijst-1" target="_blank" rel="noopener noreferrer" class="text-[#4DD0E1] hover:underline">
        → Bekijk de uitgebreide prijslijst op HomeCareService
      </a>
    `,
    externalLink: "https://www.homecareservice.me",
    phoneBE: "0486 998 995",
    phoneNL: "085 115 5750",
    phoneBELink: "+32486998995",
    phoneNLLink: "+31851155750"
  },
  "glazenwassen": {
    title: "Glazenwassen Traditioneel",
    subtitle: "Klassiek glazenwassen voor kristalheldere ramen",
    icon: CheckCircle,
    image: "https://images.unsplash.com/photo-1492724219889-989d2306de19?w=1200",
    intro: "Naast osmose ramenwassen bieden wij ook traditioneel glazenwassen aan. Deze methode is ideaal voor specifieke situaties zoals binnenkant van ramen of wanneer handmatig nawerken gewenst is.",
    benefits: [
      { icon: Sparkles, title: "Gedetailleerd Werk", text: "Handmatig nawerken voor perfecte hoeken en randen." },
      { icon: CheckCircle, title: "Binnenkant Specialisme", text: "Ideaal voor binnenramen zonder waterdruppels." },
      { icon: Users, title: "Ervaren Vakmensen", text: "Jarenlange ervaring met alle soorten glas." },
      { icon: Shield, title: "Betrouwbaar", text: "Dezelfde kwaliteit als u van ons gewend bent." }
    ],
    whatWeOffer: [
      "Traditioneel glazenwassen met trekker en zeem",
      "Binnenkant van ramen",
      "Spiegels en glaspartijen",
      "Moeilijk bereikbare hoeken",
      "Combinatie met osmose voor buitenkant",
      "Kantoren, winkels en woningen"
    ],
    pricing: `
      <strong>Prijzen per raam (excl. BTW):</strong><br><br>
      <strong>Standaard raam (tot 1,5m²):</strong><br>
      • Binnen: €3,00<br><br>
      <strong>Groot raam (1,5-3m²):</strong><br>
      • Binnen: €5,00<br><br>
      <strong>Extra groot raam (≥ 4m², etalages, puien):</strong><br>
      • Binnen: €2,00–€2,50/m²<br>
      • Binnen + buiten: €3,00–€4,00/m²<br><br>
      Buitenkant wordt altijd met osmose gereinigd.
    `
  },
  "sanering-desinfectie": {
    title: "Sanering & Desinfectieschoonmaak",
    subtitle: "Professionele schoonmaak na verstopte toiletten, buisbreuk en afvalwateroverlast",
    icon: ShieldAlert,
    image: "https://images.unsplash.com/photo-1642649238366-9b560a5519b9?crop=entropy&cs=srgb&fm=jpg&w=1200",
    intro: "Waterschade door een verstopt toilet, gesprongen leiding of rioolbreuk is niet alleen vervelend — het kan ook een ernstig gezondheidsrisico vormen. Afvalwater bevat bacteriën, virussen en schimmels die professioneel verwijderd moeten worden. Clean-Force biedt snelle en grondige sanering en desinfectieschoonmaak zodat uw ruimte weer veilig en hygiënisch is.",
    benefits: [
      { icon: Clock, title: "Snelle Inzet", text: "Bij calamiteiten staan wij snel paraat. Wij begrijpen dat elke minuut telt bij wateroverlast." },
      { icon: Shield, title: "Gecertificeerde Desinfectie", text: "Wij werken met professionele desinfectiemiddelen die bacteriën, virussen en schimmels effectief elimineren." },
      { icon: Users, title: "Gespecialiseerd Team", text: "Onze medewerkers zijn opgeleid in sanering na wateroverlast en werken met de juiste beschermingsmiddelen." },
      { icon: ThumbsUp, title: "Volledig Herstel", text: "Van leegpompen tot desinfectie en geurbestrijding — wij zorgen dat uw ruimte weer 100% schoon en veilig is." }
    ],
    whatWeOffer: [
      "Inspectie en beoordeling van de schade",
      "Verwijderen van vervuild water en afvalstoffen",
      "Grondige reiniging van alle aangetaste oppervlakken",
      "Professionele desinfectie met gecertificeerde middelen",
      "Schimmelpreventie en geurbestrijding",
      "Reiniging en desinfectie van sanitaire voorzieningen",
      "Schoonmaak van vloeren, muren en plinten na lekkage",
      "Droging en ventilatie advisering",
      "Documentatie en rapportage voor verzekeringsclaims",
      "Nazorg en controle na behandeling"
    ],
    pricing: `
      <strong>TARIEVEN SANERING & DESINFECTIE (excl. BTW):</strong><br>
      <span style="color: #A1A1AA;">Prijzen zijn afhankelijk van de omvang en ernst van de vervuiling. Hieronder vindt u onze richtprijzen.</span><br><br>
      <hr style="border-color: rgba(255,255,255,0.2); margin: 16px 0;">
      <strong>Starttarief (eerste uur, incl. voorrijkosten):</strong> €149,00<br>
      <span style="color: #A1A1AA;">Inclusief inspectie, materialen en eerste werkzaamheden.</span><br><br>
      <strong>Vervolg per kwartier:</strong> €32,50<br><br>
      <hr style="border-color: rgba(255,255,255,0.2); margin: 16px 0;">
      <strong>PAKKET TARIEVEN:</strong><br><br>
      <strong>Toilet verstopping + desinfectie:</strong> vanaf €175,00<br>
      <span style="color: #A1A1AA;">Inclusief ontstopping, reiniging en desinfectie van sanitaire ruimte.</span><br><br>
      <strong>Buisbreuk / lekkage — kleine ruimte (tot 20m²):</strong> vanaf €350,00<br>
      <span style="color: #A1A1AA;">Verwijderen vervuild water, reiniging, desinfectie en geurbestrijding.</span><br><br>
      <strong>Rioolwater overlast — middelgrote ruimte (20-60m²):</strong> vanaf €750,00<br>
      <span style="color: #A1A1AA;">Complete sanering inclusief leegpompen, reiniging en professionele desinfectie.</span><br><br>
      <strong>Grote sanering (>60m² of meerdere ruimtes):</strong> offerte op maat<br>
      <span style="color: #A1A1AA;">Wij komen gratis langs voor een inspectie en maatwerkofferte.</span><br><br>
      <hr style="border-color: rgba(255,255,255,0.2); margin: 16px 0;">
      <strong>TOESLAGEN:</strong><br>
      • Avond (18:00 - 22:00): +25%<br>
      • Weekend: +50%<br>
      • Spoed / feestdagen: +75%<br><br>
      <span style="color: #A1A1AA;">Tip: Veel saneringskosten worden vergoed door uw inboedel- of opstalverzekering. Wij leveren de benodigde documentatie voor uw claim.</span>
    `
  },
  "bouwoplevering": {
    title: "Bouwoplevering Schoonmaak",
    subtitle: "Instapklare opleveringsschoonmaak na nieuwbouw of renovatie",
    icon: HardHat,
    image: "https://images.unsplash.com/photo-1759694920759-6c5537e501e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxyZW5vdmF0aW9uJTIwY2xlYW5pbmclMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzYzNTUwMDV8MA&ixlib=rb-4.1.0&q=85",
    intro: "Na bouw- of renovatiewerken zit uw pand vol bouwstof, cementresten, verfspetters en beschermfolie. Clean-Force levert een instapklare opleveringsschoonmaak zodat u of uw klant direct in een schoon en fris pand kan trekken. Van nieuwbouwappartementen tot volledig gerenoveerde woningen.",
    benefits: [
      { icon: Sparkles, title: "Grondige Stofverwijdering", text: "Bouwstof nestelt zich overal — wij verwijderen het uit elke hoek, richel en ventilatierooster." },
      { icon: Shield, title: "Professionele Middelen", text: "Cementresten, kalkaanslag en verfspetters worden met specialistische producten verwijderd zonder schade." },
      { icon: Clock, title: "Tijdige Oplevering", text: "Wij werken snel en efficiënt zodat uw opleverdatum niet in gevaar komt." },
      { icon: ThumbsUp, title: "Instapklaar Resultaat", text: "Uw pand wordt afgeleverd alsof het nooit een bouwplaats is geweest. Direct bewoonbaar." }
    ],
    whatWeOffer: [
      "Verwijderen van grove bouwresten en beschermfolie",
      "Intensieve stofverwijdering van alle oppervlakken",
      "Reiniging van ramen binnenkant (incl. kozijnen en dorpels)",
      "Verwijderen van cement-, kalk- en verfspetters",
      "Schoonmaken van sanitair (toiletten, wastafels, douches, kranen)",
      "Grondige keukenreiniging (kasten binnen/buiten, apparatuur, aanrecht)",
      "Vloeren reinigen en dweilen (tegels, parket, laminaat)",
      "Plinten, deurkozijnen en lichtschakelaars afnemen",
      "Trapleuningen en balustrades reinigen",
      "Ventilatieroosteren en radiatoren stofvrij maken"
    ],
    pricing: `
      <strong>TARIEVEN BOUWOPLEVERING SCHOONMAAK (excl. BTW):</strong><br><br>
      
      <strong>Uurtarief gespecialiseerde schoonmaak:</strong><br>
      Vanaf €45,00/uur per medewerker<br><br>
      
      <hr style="border-color: rgba(255,255,255,0.2); margin: 16px 0;">
      
      <strong>Richtprijs instapklare opleveringsschoonmaak:</strong><br>
      Vanaf €6,00/m²<br>
      <span style="color: #A1A1AA;">Afhankelijk van mate van vervuiling, bereikbaarheid en aantal ramen.</span><br><br>
      
      <hr style="border-color: rgba(255,255,255,0.2); margin: 16px 0;">
      
      <strong>VOORBEELDEN (indicatie excl. BTW):</strong><br><br>
      • Appartement 80m²: vanaf €480<br>
      • Woning 120m²: vanaf €720<br>
      • Grote woning 200m²: vanaf €1.200<br>
      • Kantoorpand 500m²: offerte op maat<br><br>
      
      <hr style="border-color: rgba(255,255,255,0.2); margin: 16px 0;">
      
      Voorrijkosten: gratis tot 15 km<br>
      <span style="color: #A1A1AA;">Bij zware vervuiling (bijv. na volledige renovatie met stucwerk en slijpwerk) kan de prijs per m² hoger liggen. Wij bekijken dit graag samen met u.</span>
    `
  },
  "schoonmaak-na-verhuur": {
    title: "Schoonmaak na Verhuur of Verkoop",
    subtitle: "Woning instapklaar opleveren na verhuur, verkoop of verhuis",
    icon: KeyRound,
    image: "https://images.unsplash.com/photo-1757742690834-aa581b9f53b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxlbXB0eSUyMGFwYXJ0bWVudCUyMGRlZXAlMjBjbGVhbmluZyUyMG1vdmUlMjBvdXR8ZW58MHx8fHwxNzc2MzU0OTg1fDA&ixlib=rb-4.1.0&q=85",
    intro: "Bij het verlaten van een huurwoning, verkochte woning of na een verhuis verwacht de volgende bewoner of eigenaar een schone en instapklare woning. Clean-Force zorgt voor een grondige dieptereiniging zodat u uw borg terugkrijgt of uw woning een verzorgde indruk maakt bij overdracht.",
    benefits: [
      { icon: KeyRound, title: "Borg Teruggarantie", text: "Een professioneel schone woning vergroot de kans op volledige teruggave van uw waarborgsom aanzienlijk." },
      { icon: Shield, title: "Dieptereiniging", text: "Niet alleen een oppervlakkige schoonmaak — wij reinigen kasten, apparatuur, sanitair en vloeren tot in de details." },
      { icon: Users, title: "Ervaren Team", text: "Ons team heeft ruime ervaring met verhuis- en opleveringsschoonmaak voor zowel huurders als verkopers." },
      { icon: ThumbsUp, title: "Instapklaar", text: "De volgende bewoner kan direct intrekken zonder zelf nog te hoeven schoonmaken." }
    ],
    whatWeOffer: [
      "Dieptereiniging keuken (oven, koelkast, afzuigkap, kasten binnen/buiten)",
      "Sanitair grondig reinigen en ontkalken (toilet, douche, bad, wastafels)",
      "Alle vloeren stofzuigen en dweilen",
      "Ramen binnenkant wassen inclusief kozijnen",
      "Kasten en lades binnenkant reinigen",
      "Plinten, deurkozijnen en deurklinken afnemen",
      "Lichtschakelaars en stopcontacten reinigen",
      "Spinnenwebben verwijderen",
      "Radiatoren en ventilatieroosters afstoffen",
      "Balkon of terras vegen (indien van toepassing)"
    ],
    pricing: `
      <strong>TARIEVEN SCHOONMAAK NA VERHUUR/VERKOOP (excl. BTW):</strong><br><br>
      
      <strong>Uurtarief gespecialiseerde schoonmaak:</strong><br>
      Vanaf €45,00/uur per medewerker<br><br>
      
      <strong>Richtprijs per m²:</strong><br>
      Vanaf €4,00/m²<br>
      <span style="color: #A1A1AA;">Geldt bij normale vervuiling. Bij zware vervuiling, nicotine of veel achtergelaten inboedel wordt per uur of forfait op maat gerekend.</span><br><br>
      
      <hr style="border-color: rgba(255,255,255,0.2); margin: 16px 0;">
      
      <strong>PAKKETPRIJZEN PER WONINGTYPE (vanaf, excl. BTW):</strong><br><br>
      • Studio / 1 slaapkamer (tot 50m²): vanaf €250<br>
      • Appartement 2-3 slaapkamers (50-100m²): vanaf €375<br>
      • Gezinswoning (100-150m²): vanaf €550<br>
      • Grote woning 5-6 kamers (150-250m²): vanaf €850<br>
      • Villa of zeer grote woning (>250m², 6+ kamers): vanaf €1.250<br>
      <span style="color: #A1A1AA;">Bij sterke vervuiling of veel achtergelaten inboedel kan dit oplopen tot €2.000–€3.000+.</span><br><br>
      
      <hr style="border-color: rgba(255,255,255,0.2); margin: 16px 0;">
      
      <strong>TOESLAGEN:</strong><br>
      • Rokerswoning (nicotine): +25%<br>
      • Huisdieren (extra haar/geur): +15%<br>
      • Ernstige verwaarlozing: offerte op maat<br><br>
      
      Voorrijkosten: gratis tot 15 km
    `
  },
  "deepclean": {
    title: "Deep Clean Schoonmaak",
    subtitle: "Grondige dieptereiniging van normaal tot extreem vervuild",
    icon: Brush,
    image: "https://images.unsplash.com/photo-1758273238415-01ec03d9ef27?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBkZWVwJTIwY2xlYW5pbmclMjBob21lJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc2MzU1Njc1fDA&ixlib=rb-4.1.0&q=85",
    beforeAfter: {
      before: "https://customer-assets.emergentagent.com/job_clean-relaunch-1/artifacts/i8nfj1as_image-5.png",
      after: "https://customer-assets.emergentagent.com/job_clean-relaunch-1/artifacts/b93z4b99_image-4.png"
    },
    intro: "Een deep clean gaat verder dan een standaard schoonmaak. Wij reinigen uw woning of pand van boven tot onder, tot in de kleinste details. Of het nu gaat om een reguliere grondige schoonmaakbeurt of een zwaar vervuilde woning — Clean-Force maakt het weer helemaal schoon en fris.",
    benefits: [
      { icon: Sparkles, title: "Tot in de Details", text: "Achter apparaten, in kasten, boven deuren, onder meubels — geen plek wordt overgeslagen." },
      { icon: Shield, title: "Professionele Middelen", text: "Wij gebruiken industriële schoonmaakmiddelen die diep reinigen zonder uw materialen te beschadigen." },
      { icon: Clock, title: "Flexibel Inplanbaar", text: "Eenmalig of periodiek. U bepaalt wanneer en hoe vaak. Wij stemmen af op uw wensen." },
      { icon: Users, title: "Ervaren Specialisten", text: "Ons team is getraind in het aanpakken van zware vervuiling, nicotine, vetaanslag en verwaarlozing." }
    ],
    whatWeOffer: [
      "Alle vloeren grondig reinigen (stofzuigen, dweilen, machine-reiniging)",
      "Keuken dieptereiniging (oven, koelkast, afzuigkap, kasten, tegels achter aanrecht)",
      "Sanitair grondig reinigen en ontkalken (toilet, douche, bad, voegen)",
      "Ramen binnenkant wassen inclusief kozijnen en dorpels",
      "Alle kasten en lades binnenkant reinigen",
      "Plinten, deurkozijnen, deurklinken en lichtschakelaars",
      "Radiatoren en ventilatieroosters grondig reinigen",
      "Spinnenwebben verwijderen in alle ruimtes",
      "Achter en onder apparaten/meubels reinigen",
      "Vetaanslag en nicotine verwijderen (bij extreme vervuiling)",
      "Schimmel behandelen in voegen en hoeken",
      "Muren en plafonds reinigen (bij zware vervuiling)"
    ],
    pricing: `
      <strong>DEEP CLEAN TARIEVEN (excl. BTW):</strong><br><br>
      
      <strong style="color: #4DD0E1;">NORMALE DEEP CLEAN</strong><br>
      <span style="color: #A1A1AA;">Reguliere grondige schoonmaak, woning in redelijke staat</span><br><br>
      • Uurtarief: vanaf €50,00/uur per medewerker<br>
      • Richtprijs: vanaf €5,00–€6,00/m²<br><br>
      
      <strong>Indicatieprijzen normaal:</strong><br>
      • Appartement: vanaf €500–€1.000<br>
      • Woning: vanaf €800–€1.800<br><br>
      
      <hr style="border-color: rgba(255,255,255,0.2); margin: 16px 0;">
      
      <strong style="color: #F87171;">EXTREEM VERVUILD / VERWAARLOOSD / HUURSCHADE</strong><br>
      <span style="color: #A1A1AA;">Zware vervuiling, nicotine, verwaarlozing, hoarding, etc.</span><br><br>
      • Uurtarief: vanaf €60,00/uur per medewerker<br>
      • Richtprijs: vanaf €10,00/m²<br><br>
      
      <strong>Indicatieprijzen extreem:</strong><br>
      • Appartement: €900–€1.800+<br>
      • Woning/villa: €1.400–€3.500+<br>
      <span style="color: #A1A1AA;">Altijd offerte op maat na bezichtiging of foto's.</span><br><br>
      
      <hr style="border-color: rgba(255,255,255,0.2); margin: 16px 0;">
      
      <strong>TOESLAGEN:</strong><br>
      • Rokerswoning (nicotine op muren/ramen): +25%<br>
      • Huisdieren met veel haar/geur: +15%<br>
      • Ernstige verwaarlozing / veel inboedel: offerte op maat<br>
      <span style="color: #A1A1AA;">Totale kosten kunnen oplopen tot €2.000–€5.000+ bij opruiming + deep cleaning samen.</span><br><br>
      
      <hr style="border-color: rgba(255,255,255,0.2); margin: 16px 0;">
      
      Voorrijkosten: gratis tot 15 km
    `
  }
};

const ServicePage = () => {
  const { serviceSlug } = useParams();
  const service = servicesData[serviceSlug];
  const { t, language, basePath } = useLanguage();

  // SEO: use basePath (language prefix already stripped by LanguageProvider)
  const seoPath = `/diensten/${serviceSlug}`;
  const meta = getPageMeta(seoPath, language);
  const serviceSchema = buildServiceSchema(serviceSlug, language);
  const faqSchema = buildFaqSchema(serviceSlug, language);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceSlug]);

  if (!service) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-white mb-4">404</h1>
          <Link to="/" className="text-[#4DD0E1] hover:underline">
            {t.servicePage.backToHome}
          </Link>
        </div>
      </div>
    );
  }

  // Get translated title from translations if available
  const serviceKeyMap = {
    "vakantiehuizen": "vacation",
    "kantoorschoonmaak": "office",
    "industriele-reiniging": "industrial",
    "ramenwassen-osmose": "osmosis",
    "zonnepanelen-reiniging": "solar",
    "wasserijdiensten": "laundry",
    "glazenwassen": "windows",
    "sanering-desinfectie": "sanitation",
    "bouwoplevering": "construction",
    "schoonmaak-na-verhuur": "moveout",
    "deepclean": "deepclean"
  };
  const serviceKey = serviceKeyMap[serviceSlug];
  const translatedTitle = serviceKey ? t.services[serviceKey]?.title : service.title;
  const translatedDesc = serviceKey ? t.services[serviceKey]?.description : service.subtitle;

  // Get full translated content for non-Dutch languages
  const langTranslation = (language !== "nl" && serviceTranslations[language]) 
    ? serviceTranslations[language][serviceSlug] 
    : null;

  const displayTitle = langTranslation?.title || translatedTitle;
  const displaySubtitle = langTranslation?.subtitle || translatedDesc;
  const displayIntro = langTranslation?.intro || service.intro;
  const displayBenefits = langTranslation?.benefits 
    ? langTranslation.benefits.map((b, i) => ({ ...b, icon: service.benefits[i]?.icon || CheckCircle }))
    : service.benefits;
  const displayWhatWeOffer = langTranslation?.whatWeOffer || service.whatWeOffer;
  const displayPricing = langTranslation?.pricing || service.pricing;

  const IconComponent = service.icon;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Seo path={seoPath} lang={language} title={meta.title} description={meta.description} jsonLd={[serviceSchema, faqSchema]} />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass py-4">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={LOGO_URL} alt="Clean-Force" className="h-8 w-auto" />
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-white hover:text-[#4DD0E1] transition-colors"
          >
            <ArrowLeft size={20} />
            {t.servicePage.backToHome}
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-24">
        <div className="h-[400px] relative">
          <img 
            src={service.image} 
            alt={displayTitle}
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            width={1200}
            height={400}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 pb-12">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="w-16 h-16 rounded-2xl bg-[#4DD0E1]/20 flex items-center justify-center mb-4">
                <IconComponent className="text-[#4DD0E1]" size={32} />
              </div>
              <h1 className="text-4xl md:text-5xl font-black text-white mb-2">
                {displayTitle}
              </h1>
              <p className="text-xl text-[#4DD0E1]">{displaySubtitle}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Intro */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mb-16"
          >
            <p className="text-lg text-[#A1A1AA] leading-relaxed">
              {displayIntro}
            </p>
          </motion.div>

          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold text-white mb-8">{t.servicePage.benefits}</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {displayBenefits.map((benefit) => (
                <div 
                  key={benefit.title}
                  className="p-6 rounded-xl bg-[#121212] border border-white/10"
                >
                  <benefit.icon className="text-[#4DD0E1] mb-4" size={28} />
                  <h3 className="text-white font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-sm text-[#A1A1AA]">{benefit.text}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* What We Offer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold text-white mb-8">{t.servicePage.whatWeOffer}</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {displayWhatWeOffer.map((item) => (
                <div 
                  key={item}
                  className="flex items-start gap-3 p-4 rounded-lg bg-[#121212]/50"
                >
                  <CheckCircle className="text-[#4DD0E1] flex-shrink-0 mt-0.5" size={20} />
                  <span className="text-[#A1A1AA]">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Before/After Slider (if available) */}
          {service.beforeAfter && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <h2 className="text-2xl font-bold text-white mb-8">
                {language === "nl" ? "Voor & Na" : language === "fr" ? "Avant & Après" : language === "de" ? "Vorher & Nachher" : "Before & After"}
              </h2>
              <BeforeAfterSlider
                beforeImage={service.beforeAfter.before}
                afterImage={service.beforeAfter.after}
                beforeLabel={language === "nl" ? "VOOR" : language === "fr" ? "AVANT" : language === "de" ? "VORHER" : "BEFORE"}
                afterLabel={language === "nl" ? "NA" : language === "fr" ? "APRÈS" : language === "de" ? "NACHHER" : "AFTER"}
              />
            </motion.div>
          )}

          {/* Pricing */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold text-white mb-8">{t.servicePage.pricing}</h2>
            {serviceSlug === "schoonmaak-na-verhuur" ? (
              <PrijstabelNaVerhuur />
            ) : (
              <div className="p-8 rounded-2xl bg-[#121212] border border-white/10">
                <div 
                  className="text-[#A1A1AA] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(displayPricing) }}
                />
              </div>
            )}
          </motion.div>

          {/* Reviews - static section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
            data-testid="service-reviews"
          >
            <h2 className="text-2xl font-bold text-white mb-8">
              {language === "nl" ? "Wat klanten zeggen" : language === "en" ? "What clients say" : language === "fr" ? "Ce que disent nos clients" : "Was Kunden sagen"}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { name: "Jan Jansen", text: { nl: "Clean-Force heeft ons kantoor perfect schoongemaakt. Professioneel, stipt en grondig. Aanrader!", en: "Clean-Force cleaned our office perfectly. Professional, punctual and thorough. Recommended!", fr: "Clean-Force a nettoyé nos bureaux parfaitement. Professionnel, ponctuel et minutieux.", de: "Clean-Force hat unser Büro perfekt gereinigt. Professionell, pünktlich und gründlich." } },
                { name: "Sophie Vermeulen", text: { nl: "Al jaren onze vaste schoonmaakpartner. De kwaliteit is altijd consistent en het team is zeer betrouwbaar.", en: "Our regular cleaning partner for years. Quality is always consistent and the team is very reliable.", fr: "Notre partenaire de nettoyage régulier depuis des années. La qualité est toujours constante.", de: "Seit Jahren unser fester Reinigungspartner. Die Qualität ist immer gleichbleibend." } },
                { name: "Peter de Vries", text: { nl: "De opleverschoonmaak van onze huurwoning was perfect. Borg volledig teruggekregen. Top service!", en: "The move-out cleaning of our rental was perfect. Got our full deposit back. Top service!", fr: "Le nettoyage de remise de notre location était parfait. Caution intégralement récupérée.", de: "Die Übergabereinigung unserer Mietwohnung war perfekt. Kaution vollständig zurück." } },
              ].map((review) => (
                <div
                  key={review.name}
                  className="p-6 rounded-xl bg-[#121212] border border-white/10"
                  data-testid={`review-${review.name.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-[#FBBC04] text-[#FBBC04]" />
                    ))}
                  </div>
                  <p className="text-sm text-[#A1A1AA] italic mb-4">
                    "{review.text[language] || review.text.nl}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#4DD0E1]/20 flex items-center justify-center">
                      <span className="text-[#4DD0E1] font-bold text-xs">{review.name[0]}</span>
                    </div>
                    <span className="text-white text-sm font-medium">{review.name}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 text-center">
              <a
                href="https://www.google.com/maps/place/Clean-Force+BV"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-[#4DD0E1] hover:underline"
                data-testid="service-google-reviews-link"
              >
                {language === "nl" ? "Bekijk alle reviews op Google" : language === "en" ? "View all reviews on Google" : language === "fr" ? "Voir tous les avis sur Google" : "Alle Bewertungen auf Google ansehen"}
                <ChevronRight size={16} />
              </a>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center p-12 rounded-2xl bg-gradient-to-r from-[#4DD0E1]/10 to-transparent border border-[#4DD0E1]/20"
          >
            <h2 className="text-2xl font-bold text-white mb-4">
              {t.servicePage.interested} {displayTitle}?
            </h2>
            <p className="text-[#A1A1AA] mb-4 max-w-xl mx-auto">
              {t.servicePage.contactUs}
            </p>
            
            {/* Chat price calculation notice */}
            <div className="mb-8 p-4 rounded-xl bg-[#4DD0E1]/10 border border-[#4DD0E1]/30 max-w-lg mx-auto">
              <p className="text-[#4DD0E1] font-medium flex items-center justify-center gap-2">
                <MessageCircle size={20} />
                {t.chat.priceCalculation}
              </p>
              <p className="text-sm text-[#A1A1AA] mt-1">
                {t.chat.priceDescription}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/#offerte" className="btn-primary">
                {t.servicePage.requestQuote}
              </Link>
              <a href={`tel:${service.phoneBELink || "+32484448854"}`} className="btn-secondary flex items-center justify-center gap-2">
                <Phone size={18} />
                {service.phoneBE ? `BE: ${service.phoneBE}` : t.servicePage.callBE}
              </a>
              <a href={`tel:${service.phoneNLLink || "+31851155757"}`} className="btn-secondary flex items-center justify-center gap-2">
                <Phone size={18} />
                {service.phoneNL ? `NL: ${service.phoneNL}` : t.servicePage.callNL}
              </a>
              <a href="tel:+31611284300" className="btn-secondary flex items-center justify-center gap-2">
                <Phone size={18} />
                {t.servicePage.callNLMob || "NL Mobiel: 06 11 28 43 00"}
              </a>
            </div>
            
            <p className="text-sm text-[#A1A1AA]">
              {t.servicePage.emailUs} <a href="mailto:office@clean-force.eu" className="text-[#4DD0E1] hover:underline">office@clean-force.eu</a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-[#A1A1AA] text-sm">
            © 2021 - {new Date().getFullYear()} Clean-Force BV. {t.footer.rights}
          </p>
        </div>
      </footer>

      {/* Chat Widget */}
      <ServiceChatWidget />
    </div>
  );
};

// AI Chat Widget for Service Pages
const ServiceChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}`);
  const messagesEndRef = useRef(null);
  const { t, language } = useLanguage();

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: "assistant",
        content: t.chat.welcome
      }]);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  // Only trigger on open state change. Welcome text and empty-check are intentionally excluded to prevent re-initialization.

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage,
        language: language
      });
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: t.chat.error
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-20 right-6 z-50" data-testid="chat-widget">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="absolute bottom-20 right-0 w-[500px] max-w-[calc(100vw-48px)] rounded-2xl overflow-hidden border border-white/10"
            style={{ background: "rgba(0, 0, 0, 0.9)", backdropFilter: "blur(20px)" }}
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="bg-[#4DD0E1] p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles className="text-white" size={20} />
              </div>
              <div>
                <h4 className="text-black font-bold">{t.chat.title}</h4>
                <p className="text-black/70 text-sm">{t.chat.online}</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="ml-auto text-black/70 hover:text-black"
                data-testid="chat-close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Messages */}
            <div className="h-96 overflow-y-auto p-4 space-y-4 bg-[#0A0A0A]" data-testid="chat-messages">
              {messages.map((msg, index) => (
                <div
                  key={`msg-${msg.role}-${index}`}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] p-3 rounded-2xl ${
                      msg.role === "user"
                        ? "bg-[#4DD0E1] text-black rounded-br-md"
                        : "bg-[#121212] text-white border border-white/10 rounded-bl-md"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-[#121212] text-white border border-white/10 p-3 rounded-2xl rounded-bl-md">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-[#4DD0E1] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <span className="w-2 h-2 bg-[#4DD0E1] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <span className="w-2 h-2 bg-[#4DD0E1] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 bg-[#121212] border-t border-white/10">
              <div className="flex gap-2">
                <input
                  type="text"
                  data-testid="chat-input"
                  className="flex-1 bg-[#0A0A0A] border border-white/10 text-white p-3 rounded-lg focus:outline-none focus:border-[#4DD0E1]"
                  placeholder={t.chat.placeholder}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                />
                <button
                  onClick={sendMessage}
                  disabled={isLoading || !input.trim()}
                  data-testid="chat-send"
                  className="bg-[#4DD0E1] text-black px-4 rounded-lg hover:bg-[#3bc0d1] transition-colors disabled:opacity-50"
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="chat-toggle"
        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-colors ${
          isOpen ? "bg-[#3bc0d1]" : "bg-[#4DD0E1]"
        }`}
        style={{ boxShadow: isOpen ? "none" : "0 0 20px rgba(77, 208, 225, 0.5)" }}
      >
        {isOpen ? (
          <X className="text-black" size={24} />
        ) : (
          <MessageCircle className="text-black" size={24} />
        )}
      </motion.button>
    </div>
  );
};

export default ServicePage;
